import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../services/api';
import './Products.css';

import { useProductSearch } from '../../hooks/useProductSearch';
import { useSelectedLocation } from '../../hooks/useSelectedLocation';
import { useNotification } from '../../context/NotificationContext';
import ConfirmDialog from '../../components/common/ConfirmDialog';

// Icons as SVG components
const SearchIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"></circle>
    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
  </svg>
);

const EditIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);

const MaximizeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);

const XIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const Products = () => {
  void useTranslation();
  const navigate = useNavigate();
  const selectedLocation = useSelectedLocation();
  const notification = useNotification();

  const {
    searchKeywords,
    setSearchKeywords,
    searchResultsCount,
    currentPage,
    isSearching,
    searchProducts,
    getCurrentPageProducts,
    nextPage,
    prevPage,
    totalPages
  } = useProductSearch(100, selectedLocation);

  // Form states
  const [productName, setProductName] = useState('');
  const [originalPrice, setOriginalPrice] = useState('');
  const [promoPrice, setPromoPrice] = useState('');
  const [promoStartDate, setPromoStartDate] = useState('');
  const [promoEndDate, setPromoEndDate] = useState('');
  const [promoType, setPromoType] = useState('');
  const [productId, setProductId] = useState(null);
  const [limitQty, setLimitQty] = useState('');
  const [priceUnit, setPriceUnit] = useState('lb.');
  const [largeImgSrc, setLargeImgSrc] = useState('');

  // Error states
  const [originalPriceError, setOriginalPriceError] = useState('');

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    // Initial search when location changes
  }, [selectedLocation]);

  const promoTypeLabels = {
    'PRICE_DISCOUNT': '$ off on Original Price',
    'PERCENTAGE_DISCOUNT': '% off on Original Price',
    'BOGO': 'Buy 1 Get 1 Free',
  };

  const handleSearchSubmit = async (event) => {
    event.preventDefault();
    searchProducts(searchKeywords);
    clearForm();
  };

  const handleRowClick = (product) => {
    setSelectedProduct(product);
    setLargeImgSrc(product.original_img_large_src || '');
    setProductName(product.product_name || '');
    setOriginalPrice(product.original_price?.toString() || '');
    setPromoPrice(product.promo_price?.toString() || '');
    setPromoStartDate(product.promo_start_date ? product.promo_start_date.split('T')[0] : '');
    setPromoEndDate(product.promo_end_date ? product.promo_end_date.split('T')[0] : '');
    setPromoType(product.promo_type || '');
    if (product._id || product.id) {
      setProductId(product._id || product.id);
    }
    setLimitQty(product.limit_qty?.toString() || '');
    setPriceUnit(product.price_unit || 'lb.');
    setShowEditPanel(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Validation
    if (originalPrice.trim() === '') {
      setOriginalPriceError('Original price is required');
      return;
    }

    setIsUpdating(true);

    const productUpdate = {
      original_price: originalPrice,
      promo_price: promoPrice.trim() === '' ? null : promoPrice,
      promo_start_date: promoStartDate || null,
      promo_end_date: promoEndDate || null,
      promo_type: promoType,
      limit_qty: limitQty || null,
      price_unit: priceUnit
    };

    try {
      await apiClient.put(`/api/products/${productId}`, productUpdate);
      notification.success('Product updated successfully!');
      searchProducts(searchKeywords);
    } catch (error) {
      notification.error('Error updating product. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteDialogOpen(false);
    try {
      await apiClient.delete(`/api/products/${selectedProduct._id || selectedProduct.id}`);
      notification.success('Product deleted successfully!');
      clearForm();
      searchProducts(searchKeywords);
    } catch (error) {
      notification.error('Error deleting product.');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleMaximize = () => {
    // Navigate to AddProduct in edit mode, passing the product data via state
    navigate(`/addproduct?edit=${productId}`, {
      state: { product: selectedProduct }
    });
  };

  const clearForm = () => {
    setLargeImgSrc('');
    setSelectedProduct(null);
    setProductName('');
    setOriginalPrice('');
    setPromoPrice('');
    setPromoStartDate('');
    setPromoEndDate('');
    setPromoType('');
    setProductId(null);
    setOriginalPriceError('');
    setShowEditPanel(false);
    setPriceUnit('lb.');
    setLimitQty('');
  };

  const calculateSavings = () => {
    if (promoPrice && originalPrice && promoType !== 'BOGO') {
      const savings = Math.round((1 - parseFloat(promoPrice) / parseFloat(originalPrice)) * 100);
      if (savings > 0) return `Save ${savings}%`;
    }
    return null;
  };

  return (
    <div className="product-catalog-container">

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete this product?"
        description="This action cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Toolbar */}
      <div className="product-catalog-toolbar">
        <div className="toolbar-left">
          <div className="toolbar-title">
            <h1>Product Catalog</h1>
            <p>Search and manage your products</p>
          </div>
        </div>
        <div className="toolbar-actions">
          <button
            className="action-btn secondary"
            onClick={() => navigate('/bulkuploadproducts')}
          >
            + Bulk Upload
          </button>
          <button
            className="action-btn primary"
            onClick={() => navigate('/addproduct')}
          >
            + Add New Product
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="product-catalog-content">
        <div className="product-catalog-grid">
          {/* Left Column - Search and Results */}
          <div className="product-list-column">
            {/* Search Section */}
            <div className="modern-card search-card">
              <div className="card-header">
                <h2>Search Products</h2>
              </div>
              <div className="card-body">
                <form onSubmit={handleSearchSubmit} className="search-form">
                  <div className="search-input-wrapper">
                    <SearchIcon />
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Search by product name or SKU..."
                      value={searchKeywords}
                      onChange={(e) => setSearchKeywords(e.target.value)}
                    />
                  </div>
                  <button
                    type="submit"
                    className="action-btn primary search-btn"
                    disabled={isSearching}
                  >
                    {isSearching ? 'Searching...' : 'Search'}
                  </button>
                </form>
              </div>
            </div>

            {/* Results Section */}
            <div className="modern-card results-card">
              <div className="results-header">
                <h2>
                  {searchResultsCount !== null
                    ? `${searchResultsCount} Product${searchResultsCount !== 1 ? 's' : ''} Found`
                    : 'Products'
                  }
                </h2>
              </div>

              {/* Desktop Table View */}
              <div className="products-table-container">
                {searchResultsCount > 0 ? (
                  <>
                    <table className="products-table">
                      <thead>
                        <tr>
                          <th>SKU</th>
                          <th>Product Name</th>
                          <th>Original Price</th>
                          <th>Promo Price</th>
                          <th>Promo Type</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getCurrentPageProducts().map((product, index) => (
                          <tr
                            key={product._id || product.id || index}
                            onClick={() => handleRowClick(product)}
                            className={selectedProduct === product ? 'selected' : ''}
                          >
                            <td>{product.retailer_sku_id}</td>
                            <td>{product.product_name}</td>
                            <td>${product.original_price}</td>
                            <td>{product.promo_price ? `$${product.promo_price}` : '-'}</td>
                            <td>{promoTypeLabels[product.promo_type] || product.promo_type || '-'}</td>
                            <td>
                              <button
                                className="edit-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRowClick(product);
                                }}
                              >
                                <EditIcon />
                                Edit
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {totalPages > 1 && (
                      <div className="pagination">
                        <button
                          className="pagination-btn"
                          onClick={prevPage}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                        <span className="pagination-info">{currentPage} / {totalPages}</span>
                        <button
                          className="pagination-btn"
                          onClick={nextPage}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="empty-state">
                    <div className="empty-state-icon">
                      <SearchIcon />
                    </div>
                    <h3>No products found</h3>
                    <p>Try adjusting your search query</p>
                  </div>
                )}
              </div>

              {/* Mobile Card View */}
              <div className="products-mobile-cards">
                {searchResultsCount > 0 ? (
                  <>
                    {getCurrentPageProducts().map((product, index) => (
                      <div
                        key={product._id || product.id || index}
                        className="product-mobile-card"
                        onClick={() => handleRowClick(product)}
                      >
                        <div className="product-mobile-card-header">
                          <div className="product-mobile-card-info">
                            <h3>{product.product_name}</h3>
                            <p>SKU: {product.retailer_sku_id}</p>
                          </div>
                          <button
                            className="edit-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowClick(product);
                            }}
                          >
                            <EditIcon />
                          </button>
                        </div>
                        <div className="product-mobile-card-body">
                          <span>
                            <span className="label">Original:</span>
                            <span className="value">${product.original_price}</span>
                          </span>
                          <span>
                            <span className="label">Promo:</span>
                            <span className="value">{product.promo_price ? `$${product.promo_price}` : '-'}</span>
                          </span>
                        </div>
                      </div>
                    ))}
                    {totalPages > 1 && (
                      <div className="pagination">
                        <button
                          className="pagination-btn"
                          onClick={prevPage}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                        <span className="pagination-info">{currentPage} / {totalPages}</span>
                        <button
                          className="pagination-btn"
                          onClick={nextPage}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="empty-state">
                    <div className="empty-state-icon">
                      <SearchIcon />
                    </div>
                    <h3>No products found</h3>
                    <p>Try adjusting your search query</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Edit Panel */}
          <div className={`product-edit-column ${showEditPanel ? 'active' : ''}`}>
            {!selectedProduct ? (
              <div className="modern-card">
                <div className="edit-panel-placeholder">
                  <div className="edit-panel-placeholder-icon">
                    <EditIcon />
                  </div>
                  <h3>Select a Product to Edit</h3>
                  <p>Click on any product from the list to update its pricing</p>
                </div>
              </div>
            ) : (
              <div className="product-edit-panel">
                {/* Edit Panel Header */}
                <div className="edit-panel-toolbar">
                  <div className="edit-panel-toolbar-left">
                    <button className="close-btn" onClick={clearForm}>
                      <XIcon />
                    </button>
                    <h2>Quick Edit</h2>
                  </div>
                  <button className="maximize-btn" onClick={handleMaximize} title="Open full editor">
                    <MaximizeIcon />
                  </button>
                </div>

                {/* Scrollable Form Content */}
                <div className="edit-panel-content">
                  <form onSubmit={handleSubmit} id="product-edit-form">
                    {/* Product Info - Read Only */}
                    <div className="modern-card">
                      <div className="card-header">
                        <h2>Product Information</h2>
                      </div>
                      <div className="card-body">
                        <div className="product-info-display">
                          {largeImgSrc && (
                            <div className="product-image-thumbnail">
                              <img src={largeImgSrc} alt={productName} />
                            </div>
                          )}
                          <div className="product-name-display">
                            <label className="form-label">Product Name</label>
                            <p className="product-name-value">{productName}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Pricing - Editable */}
                    <div className="modern-card">
                      <div className="card-header">
                        <h2>Pricing</h2>
                      </div>
                      <div className="card-body">
                        <div className="form-row form-row-2">
                          <div className="form-group">
                            <label className="form-label">
                              Original Price <span className="required">*</span>
                            </label>
                            <div className="form-input-with-prefix">
                              <span className="form-input-prefix">$</span>
                              <input
                                type="number"
                                step="0.01"
                                className="form-input"
                                value={originalPrice}
                                onChange={(e) => {
                                  setOriginalPrice(e.target.value);
                                  setOriginalPriceError('');
                                }}
                                placeholder="0.00"
                              />
                            </div>
                            {originalPriceError && <span className="form-hint" style={{ color: '#dc2626' }}>{originalPriceError}</span>}
                          </div>
                          <div className="form-group">
                            <label className="form-label">Promo Price</label>
                            <div className="form-input-with-prefix">
                              <span className="form-input-prefix">$</span>
                              <input
                                type="number"
                                step="0.01"
                                className="form-input"
                                value={promoPrice}
                                onChange={(e) => setPromoPrice(e.target.value)}
                                placeholder="0.00"
                                disabled={promoType === 'BOGO'}
                              />
                            </div>
                            {calculateSavings() && (
                              <span className="form-hint form-hint-success">{calculateSavings()}</span>
                            )}
                            {promoType === 'BOGO' && (
                              <span className="form-hint form-hint-info">Promo price not needed for BOGO</span>
                            )}
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label className="form-label">Promo Type</label>
                            <select
                              className="form-select"
                              value={promoType}
                              onChange={(e) => setPromoType(e.target.value)}
                            >
                              <option value="">Select promo type</option>
                              <option value="PRICE_DISCOUNT">$ off on Original Price</option>
                              <option value="PERCENTAGE_DISCOUNT">% off on Original Price</option>
                              <option value="BOGO">Buy 1 Get 1 Free</option>
                            </select>
                          </div>
                        </div>

                        <div className="form-row form-row-2">
                          <div className="form-group">
                            <label className="form-label">Start Date</label>
                            <input
                              type="date"
                              className="form-input"
                              value={promoStartDate}
                              onChange={(e) => setPromoStartDate(e.target.value)}
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">End Date</label>
                            <input
                              type="date"
                              className="form-input"
                              value={promoEndDate}
                              onChange={(e) => setPromoEndDate(e.target.value)}
                            />
                          </div>
                        </div>

                        <div className="form-row form-row-2">
                          <div className="form-group">
                            <label className="form-label">Limit Quantity</label>
                            <input
                              type="number"
                              className="form-input"
                              value={limitQty}
                              onChange={(e) => setLimitQty(e.target.value)}
                              placeholder="Max per customer"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Price Unit</label>
                            <select
                              className="form-select"
                              value={priceUnit}
                              onChange={(e) => setPriceUnit(e.target.value)}
                            >
                              <option value="ea.">ea.</option>
                              <option value="lb.">lb.</option>
                              <option value="kg">kg</option>
                              <option value="ltr">ltr</option>
                              <option value="pack of 2">pack of 2</option>
                              <option value="pack of 3">pack of 3</option>
                              <option value="pack of 6">pack of 6</option>
                              <option value="pack of 12">pack of 12</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>

                {/* Fixed Action Bar */}
                <div className="action-bar">
                  <button
                    type="button"
                    className="action-btn secondary"
                    onClick={clearForm}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    form="product-edit-form"
                    className="action-btn primary"
                    disabled={isUpdating}
                  >
                    {isUpdating ? 'Updating...' : 'Update'}
                  </button>
                  <button
                    type="button"
                    className="action-btn danger"
                    onClick={handleDelete}
                  >
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;
